import React, { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { Toaster } from 'react-hot-toast'
import { useStore } from './store/useStore'
import { RouteErrorBoundary } from './components/error-boundaries/RouteErrorBoundary'
import ErrorPage from './components/error-boundaries/ErrorPage'
import Layout from './components/Layout'
import ContactPage from './components/ContactPage'
import BlogDetail from './pages/BlogDetail'
import LoadingSpinner from './components/LoadingSpinner'

const HomePage = lazy(() => import('./pages/HomePage'))
const BirdGallery = lazy(() => import('./pages/BirdGallery'))
const GamesPage = lazy(() => import('./pages/GamesPage'))
const BlogPage = lazy(() => import('./pages/BlogPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const SignInPage = lazy(() => import('./pages/SignInPage'))
const SignUpPage = lazy(() => import('./pages/SignUpPage'))
const BirdDetail = lazy(() => import('./pages/BirdDetail'))
const AboutPage   = lazy(() => import('./pages/AboutPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const TermsPage   = lazy(() => import('./pages/TermsPage'))

function App() {
  const { isLoaded, isSignedIn } = useAuth()
  const { initialize } = useStore()

  useEffect(() => {
    initialize()
  }, [initialize])

  if (!isLoaded) {
    return <LoadingSpinner />
  }

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <div className="app">
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 5000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }}
        />

        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <HomePage />
                </Suspense>
              </RouteErrorBoundary>
            } />

            <Route path="gallery" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <BirdGallery />
                </Suspense>
              </RouteErrorBoundary>
            } />

            <Route path="games" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <GamesPage />
                </Suspense>
              </RouteErrorBoundary>
            } />

            <Route path="blog" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <BlogPage />
                </Suspense>
              </RouteErrorBoundary>
            } />

            <Route path="gallery/:id" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <BirdDetail />
                </Suspense>
              </RouteErrorBoundary>
            } />
            <Route path="/error" element={<ErrorPage />} />
            <Route path="/blog/:id" element={<BlogDetail />} />
            <Route path="/contact" element={<ContactPage />} />

            <Route path="/about" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <AboutPage />
                </Suspense>
              </RouteErrorBoundary>
            } />

            <Route path="/privacy" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <PrivacyPage />
                </Suspense>
              </RouteErrorBoundary>
            } />

            <Route path="/terms" element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <TermsPage />
                </Suspense>
              </RouteErrorBoundary>
            } />

            <Route path="profile" element={
              isSignedIn ? (
                <RouteErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <ProfilePage />
                  </Suspense>
                </RouteErrorBoundary>
              ) : (
                <Navigate to="/sign-in" replace />
              )
            } />

            <Route path="sign-in" element={
              <Suspense fallback={<LoadingSpinner />}>
                <SignInPage />
              </Suspense>
            } />

            <Route path="sign-up" element={
              <Suspense fallback={<LoadingSpinner />}>
                <SignUpPage />
              </Suspense>
            } />
          </Route>
        </Routes>
      </div>
    </Router>
  )
}

export default App